import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Megaphone,
  Users,
  Inbox,
  Linkedin,
  Target,
  Settings,
  BarChart3,
  Activity,
  ChevronRight,
  Globe,
  Mail,
  FileText,
  Database,
  GitMerge,
  Tag,
  Key,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/uiStore';
import { ROUTES } from '@/lib/routes';

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
}

interface NavGroup {
  icon: LucideIcon;
  label: string;
  children: NavItem[];
}

const NAV_MAIN: (NavItem | NavGroup)[] = [
  { to: ROUTES.DASHBOARD, icon: LayoutDashboard, label: 'Overview' },
  { to: ROUTES.CAMPAIGNS, icon: Megaphone, label: 'Campagnes' },
  { to: ROUTES.LEADS, icon: Users, label: 'Leads' },
  { to: ROUTES.INBOX, icon: Inbox, label: 'Inbox' },
  { to: ROUTES.OUTREACH, icon: Linkedin, label: 'Outreach' },
  { to: ROUTES.ENRICHERS, icon: Target, label: 'Enrichisseurs' },
  {
    icon: Settings,
    label: 'Settings',
    children: [
      { to: ROUTES.SETTINGS_DOMAINS, icon: Globe, label: 'Domaines' },
      { to: ROUTES.SETTINGS_MAIL, icon: Mail, label: 'Comptes mail' },
      { to: ROUTES.SETTINGS_TEMPLATES, icon: FileText, label: 'Templates' },
      { to: ROUTES.SETTINGS_HUBSPOT, icon: GitMerge, label: 'HubSpot' },
      { to: ROUTES.SETTINGS_BIGQUERY, icon: Database, label: 'BigQuery' },
      { to: ROUTES.SETTINGS_FORWARDERS, icon: GitMerge, label: 'Forwarders' },
      { to: ROUTES.SETTINGS_LABELS, icon: Tag, label: 'Labels' },
      { to: ROUTES.SETTINGS_KEYS, icon: Key, label: 'API keys' },
    ],
  },
  { to: ROUTES.MONITORING, icon: BarChart3, label: 'Monitoring' },
];

function isGroup(item: NavItem | NavGroup): item is NavGroup {
  return 'children' in item;
}

export default function Sidebar() {
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <aside
      className={cn(
        'flex flex-col bg-brand-dark border-r border-white/5 shrink-0 transition-all duration-200',
        sidebarOpen ? 'w-56' : 'w-14',
      )}
    >
      <button
        type="button"
        onClick={toggleSidebar}
        className="flex items-center gap-3 px-4 py-4 border-b border-white/5 min-h-[60px] w-full hover:bg-white/5 transition-colors"
        title={sidebarOpen ? 'Réduire' : 'Développer'}
      >
        <div className="w-7 h-7 rounded-lg bg-brand-primary flex items-center justify-center shrink-0">
          <Activity size={14} className="text-white" />
        </div>
        {sidebarOpen && (
          <span className="font-heading text-white font-bold text-sm leading-tight">Dispatcher</span>
        )}
      </button>

      <nav className="flex-1 px-2 py-3 overflow-y-auto space-y-0.5">
        {NAV_MAIN.map((item) => {
          if (isGroup(item)) {
            return (
              <div key={item.label}>
                <button
                  type="button"
                  onClick={() => setSettingsOpen((o) => !o)}
                  title={!sidebarOpen ? item.label : undefined}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-brand text-sm font-medium transition-colors w-full',
                    'text-white/60 hover:text-white hover:bg-white/5',
                    !sidebarOpen && 'justify-center px-2',
                  )}
                >
                  <item.icon size={15} />
                  {sidebarOpen && (
                    <>
                      <span className="flex-1 text-left">{item.label}</span>
                      <ChevronRight
                        size={13}
                        className={cn('transition-transform', settingsOpen && 'rotate-90')}
                      />
                    </>
                  )}
                </button>
                {sidebarOpen && settingsOpen && (
                  <div className="ml-4 mt-0.5 space-y-0.5 border-l border-white/10 pl-3">
                    {item.children.map((child) => (
                      <SidebarLink key={child.to} {...child} compact={false} />
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return <SidebarLink key={item.to} {...item} compact={!sidebarOpen} />;
        })}
      </nav>
    </aside>
  );
}

function SidebarLink({ to, icon: Icon, label, compact }: NavItem & { compact: boolean }) {
  return (
    <NavLink
      to={to}
      title={compact ? label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2 rounded-brand text-sm font-medium transition-colors',
          isActive ? 'bg-brand-primary text-white' : 'text-white/60 hover:text-white hover:bg-white/5',
          compact && 'justify-center px-2',
        )
      }
    >
      <Icon size={15} />
      {!compact && label}
    </NavLink>
  );
}
